import { z } from 'zod';
import { IsoDateTime, Uuid } from './common.js';
/**
 * Badge templates (module `badges`, docs/modules/badges.md, ADR 0011). Owner: task S5.
 * Pure rendering helpers (layout -> draw ops, Brother QL raster) live in ../badges/, also S5.
 */
/**
 * Supported label media. Sizes in mm as printed on the roll; the printable area in dots at 300 dpi comes from the
 * Brother QL raster command reference and lives in ../badges/media.ts (S5 copies it from the reference, never guesses).
 */
export const BadgeMedia = z.enum([
    /** DK-11204 die-cut 17 x 54 mm (name-only badge). */
    'dk11204_17x54',
    /** DK-11209 die-cut 29 x 62 mm. */
    'dk11209_29x62',
    /** DK-11202 die-cut 62 x 100 mm (photo badge). */
    'dk11202_62x100',
    /** DK-22205 continuous 62 mm, cut at 90 mm. */
    'dk22205_62x90',
]);
/** Data a text element can show. `field:<uuid>` = a sign-in or personal field's text. */
export const BadgeDataKey = z.union([
    z.enum(['visitor_name', 'first_name', 'group_name', 'site_name', 'host_name', 'date', 'time_in', 'tenant_name']),
    z.string().regex(/^field:[0-9a-f-]{36}$/),
]);
/** Position and size in mm from the top-left of the label in its printed orientation. */
const Box = {
    id: z.string().min(1).max(40),
    x: z.number().min(0).max(200),
    y: z.number().min(0).max(200),
    w: z.number().min(1).max(200),
    h: z.number().min(1).max(200),
};
export const BadgeElement = z.discriminatedUnion('type', [
    z.object({
        ...Box,
        type: z.literal('text'),
        source: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('static'), text: z.string().max(200) }),
            z.object({ kind: z.literal('data'), key: BadgeDataKey }),
        ]),
        /** Starting size in points; shrinks to fit when shrinkToFit. */
        fontSize: z.number().min(4).max(96).default(14),
        bold: z.boolean().default(false),
        align: z.enum(['left', 'center', 'right']).default('left'),
        shrinkToFit: z.boolean().default(true),
        maxLines: z.number().int().min(1).max(4).default(1),
    }),
    z.object({
        ...Box,
        type: z.literal('image'),
        source: z.enum(['photo', 'tenant_logo', 'site_logo', 'template_logo']),
        fit: z.enum(['contain', 'cover']).default('contain'),
    }),
    z.object({
        ...Box,
        type: z.literal('qr'),
        /** sign_out = the visit's sign-out code (ADR 0008 "QR codes"). */
        source: z.enum(['sign_out']),
    }),
    z.object({
        ...Box,
        type: z.literal('box'),
        strokeMm: z.number().min(0).max(5).default(0.3),
        fill: z.boolean().default(false),
    }),
]);
export const BadgeLayout = z.object({
    media: BadgeMedia,
    orientation: z.enum(['portrait', 'landscape']).default('landscape'),
    elements: z.array(BadgeElement).max(30),
});
export const BadgeTemplateStatus = z.enum(['active', 'archived']);
export const BadgeTemplate = z.object({
    id: Uuid,
    name: z.string(),
    /** null = available to every site. */
    siteId: Uuid.nullable(),
    layout: BadgeLayout,
    /** Optional logo for `template_logo` image elements. */
    logoFileId: Uuid.nullable(),
    /** Immutable snapshot that visits record when a badge is printed. */
    currentVersionId: Uuid,
    version: z.number().int(),
    status: BadgeTemplateStatus,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
/** GET /badges/templates: the site's own templates plus the tenant-wide ones. */
export const BadgeTemplateListQuery = z.object({
    siteId: Uuid.optional(),
    status: z.enum(['active', 'archived', 'all']).default('active'),
});
export const BadgeTemplateCreate = z.object({
    name: z.string().trim().min(1).max(80),
    siteId: Uuid.nullable().default(null),
    layout: BadgeLayout,
});
export const BadgeTemplateUpdate = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    layout: BadgeLayout.optional(),
    status: BadgeTemplateStatus.optional(),
});
/** POST /badges/preview: renders a layout with sample data to PDF (designer preview and test print from the portal). */
export const BadgePreviewRequest = z.object({
    layout: BadgeLayout,
    templateId: Uuid.nullable().default(null),
    sample: z.record(z.string(), z.string()).default({}),
});
/** Site-level settings of the `badges` module. */
export const BadgesSiteSettings = z.object({
    /** Template used when a group doesn't choose one. null = no badge at this site. */
    defaultTemplateId: Uuid.nullable().default(null),
    /** Kiosk warns staff when the loaded labels don't match the template's media. */
    mediaWarning: z.boolean().default(true),
});
/** Group-level settings of the `badges` module. */
export const BadgesGroupSettings = z.object({
    /** never = no badge; auto = print at sign-in; ask = the visitor chooses. */
    print: z.enum(['never', 'auto', 'ask']).default('auto'),
    /** null = the site's default template. */
    templateId: Uuid.nullable().default(null),
});
/** Kiosk step config for the `badge` step. */
export const KioskBadgeStepConfig = z.object({
    print: z.enum(['auto', 'ask']),
    templateVersionId: Uuid,
    layout: BadgeLayout,
    /**
     * Key (base64url) for this tenant's sign-out QR so an offline kiosk can sign the badge it prints:
     * signature = first 16 bytes of HMAC-SHA256(key, visitId), base64url (see badges/qr.ts).
     */
    qrKey: z.string().optional(),
    /** The site's "media warning" setting: when on, the kiosk shows a clear message for wrong or missing labels. Absent (older server) = on. */
    mediaWarning: z.boolean().optional(),
    /** Signed links (cached by file id on the kiosk) for logos the layout uses. */
    images: z.object({
        tenantLogo: z.object({ fileId: Uuid, url: z.string() }).nullable(),
        siteLogo: z.object({ fileId: Uuid, url: z.string() }).nullable(),
        templateLogo: z.object({ fileId: Uuid, url: z.string() }).nullable(),
    }),
});
/** What the kiosk reports about the badge step with a sign-in event. */
export const KioskBadgeStepResult = z.object({
    templateVersionId: Uuid,
    printed: z.boolean(),
    /** Printer status code when printing failed. */
    error: z.string().max(60).nullable().default(null),
});
//# sourceMappingURL=badges.js.map